import { FC, useEffect, useMemo, useState } from "react";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import { getRecordMetadataColumns } from "@/components/tables/recordMetadataColumns";
import { useTranslation } from "react-i18next";
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import DropDownSubjectSearch from "@/components/forms/inputs/dropDownSubjectSearch";
import style from "@/components/userAdministration/companyManagerTable.module.css";
import DropDown from "@/components/forms/inputs/dropDown";
import { api } from "@/helper/api";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import GenericNotification from "@/components/notification/genericNotification";
import { useSelector } from "react-redux";
import { RootState } from "@/components/state/store";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import NavigateToSubjectOverviewLink from "@/components/subjectOverview/navigateToSubjectOverviewLink";
import { FormFieldLabel } from "@/components/forms/inputs/formFieldLabel";
import { useModalDismiss } from "@/helper/customHooks/useModalDismiss";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";
import { companyManagerEntityAdapter } from "@/components/userAdministration/companyManagerEntityAdapter";

interface Props {
  companySubjectId?: any;
  companyId?: any;
  subdomain?: any;
  triggerReload?: any;
  onManagersChanged?: () => void;
  currentManagerType?: any;
}

type ManagerValidationTarget = "subject" | "managerType" | "departments";

const CompanyManagerTable: FC<Props> = ({
  companySubjectId,
  companyId,
  subdomain,
  triggerReload,
  onManagersChanged,
  currentManagerType,
}) => {
  const { t } = useTranslation();
  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
  const [refreshCompanyManagerTable, setRefreshCompanyManagerTable] =
    useState<boolean>(false);
  const [selectedManagerSubject, setSelectedManagerSubject] =
    useState<string>("");
  const [subjectSearchAllCompanies, setSubjectSearchAllCompanies] =
    useState<boolean>(false);
  const [selectedManagerTypeOption, setSelectedManagertypeOption] =
    useState<any>();
  const { handleError, errorModal } = useErrorModal();
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<any>("");
  const [notificationVariant, setNotificationVariant] = useState<
    "success" | "error" | "warning" | "info" | undefined
  >();
  const [departmentOptions, setDepartmentOptions] = useState<any>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<any>([]);
  const [editingManager, setEditingManager] = useState<any>(null);
  const [validationErrors, setValidationErrors] = useState<
    ManagerValidationTarget[]
  >([]);
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField<
      Record<string, never>,
      ManagerValidationTarget
    >({ fieldOrder: [] });

  const isEditMode = Boolean(editingManager);
  const title = isEditMode
    ? `${t("edit")} ${editingManager?.subject?.name ?? ""}`
    : t("add_new_manager");
  const submitButtonText = isEditMode ? t("save") : t("add");

  const managerCompany = useSelector((state: RootState) => state.login);
  const selectedChildCompany = useSelector((state: RootState) => state.parent);

  const resetStateOnCloseNotification = () => {
    setShowNotification(false);
    setNotificationMessage("");
  };

  const managerOptions = [
    { value: "standard_manager", label: t("standard_manager") },
    { value: "department_manager", label: t("department_manager") },
  ];

  const getDepartments = () => {
    const additionalUrlParameter: any = {};
    if (subdomain === "admin") {
      additionalUrlParameter["impersonate_subject"] = companySubjectId;
    }
    api.genericApiRequest({
      entity: "department",
      method: "get",
      parametersToRender: { additionalUrlParameters: additionalUrlParameter },
      successHandler: (res: any) => {
        const data = res.data.results;
        const departmentOptions = [];
        for (const key in data) {
          departmentOptions.push({
            value: data[key].id,
            label: data[key].name,
          });
        }
        setDepartmentOptions(departmentOptions);
      },
      failHandler: (error: any) => {
        console.log(error);
      },
    });
  };

  useEffect(() => {
    if (
      openCreateModal &&
      selectedManagerTypeOption?.value === "department_manager"
    ) {
      getDepartments();
    }
  }, [openCreateModal, selectedManagerTypeOption]);

  useEffect(() => {
    if (triggerReload === undefined) return;
    setRefreshCompanyManagerTable((prev) => !prev);
  }, [triggerReload]);

  const managerColumns = useMemo(
    () => [
      {
        header: t("id"),
        accessorKey: "id",
        id: "id",
        enableHiding: !companySubjectId ? false : true,
        visibleInShowHideMenu: !companySubjectId ? false : true,
      },
      {
        header: t("name"),
        accessorKey: "subject.name",
        id: "name",
        customColumnFilterId: "name",
        customSortingKey: "name",
        accessorFn: (row: any) => {
          if (subdomain === "admin") {
            return (
              <NavigateToSubjectOverviewLink
                subjectId={row.subject.id}
                label={row.subject.name}
              />
            );
          }
          return row.subject.name;
        },
      },
      {
        header: t("company"),
        accessorKey: "subject.company.name",
        id: "company",
        customColumnFilterId: "company_name",
        customSortingKey: "company_name",
        accessorFn: (row: any) => {
          if (subdomain === "admin") {
            return (
              <NavigateToSubjectOverviewLink
                companyId={row.subject.company.id}
                label={row.subject.company.name}
              />
            );
          }
          return row.subject.company.name;
        },
      },
      {
        header: t("type"),
        accessorKey: "manager_type",
        filterVariant: "select",
        filterSelectOptions: managerOptions,
        enableForCreate: true,
        selectOptions: [
          {
            value: "standard_manager",
            label: t("standard_manager"),
          },
          {
            value: "department_manager",
            label: t("department_manager"),
          },
        ],
        id: "manager_type",
        setNoFilterParameter: true,
        accessorFn: (row: any) => {
          const managerType = t(row.manager_type);
          if (row.is_ancestor_manager) {
            return `${managerType} (${t("inherited")})`;
          }
          return managerType;
        },
      },
      {
        header: t("departments"),
        id: "managed_department_set",
        enableColumnFilter: false,
        enableForCreate: true,

        accessorFn: (row: any) => {
          const departments: any = [];
          if (row.managed_department_set) {
            for (const key in row.managed_department_set) {
              if (row.managed_department_set[key].name) {
                departments.push(row.managed_department_set[key].name);
              }
            }
          }
          return departments.join(", ");
        },
      },
      ...(subdomain === "admin" // the api delivers the tracking fields only on the admin subdomain
        ? getRecordMetadataColumns(t, [
            "created_on",
            "created_by",
            "modified_on",
            "modified_by",
          ])
        : []),
    ],
    [t, companySubjectId, subdomain]
  );

  const handleSubjectSelect = (selectedOption: any) => {
    if (
      selectedOption &&
      typeof selectedOption === "object" &&
      selectedOption.value
    ) {
      setSelectedManagerSubject(selectedOption.value);
      setValidationErrors((current) =>
        current.filter((target) => target !== "subject"),
      );
    }
  };
  const handleCheckboxChange = (event: any) => {
    setSubjectSearchAllCompanies(event.target.checked);
  };

  const onCloseDialog = () => {
    setOpenCreateModal(false);
    setSubjectSearchAllCompanies(false);
    setValidationErrors([]);
  };

  const handleOpenCreate = () => {
    setEditingManager(null);
    setSelectedManagertypeOption(undefined);
    setSelectedDepartments([]);
    setSelectedManagerSubject("");
    setValidationErrors([]);
    setOpenCreateModal(true);
  };

  //TODO @BEN if department manager gets reactivated also add managed_department_set to table

  const handleSelectedManagerOptionChange = (selectedOption: any) => {
    setSelectedManagertypeOption(selectedOption);
    setValidationErrors((current) =>
      current.filter(
        (target) => target !== "managerType" && target !== "departments",
      ),
    );
    if (selectedOption?.value === "standard_manager") {
      setSelectedDepartments([]);
    }
  };
  const handleSelectedDepartmentsChange = (selectedOption: any) => {
    if (!selectedOption || selectedOption.length === 0) {
      return;
    }
    setSelectedDepartments(selectedOption);
    setValidationErrors((current) =>
      current.filter((target) => target !== "departments"),
    );
  };
  const handleOpenEdit = (row: any) => {
    const original = row.original;
    setEditingManager(original);
    const typeOption = managerOptions.find(
      (option) => option.value === original.manager_type
    );
    setSelectedManagertypeOption(typeOption);
    const prefilledDepartments = (original.managed_department_set || [])
      .filter((dept: any) => dept && dept.id)
      .map((dept: any) => ({ value: dept.id, label: dept.name }));
    setSelectedDepartments(prefilledDepartments);
    setSelectedManagerSubject(original.subject?.id ?? "");
    setValidationErrors([]);
    setOpenCreateModal(true);
  };
  const handleSubmit = () => {
    const invalidTargets: ManagerValidationTarget[] = [];
    if (!isEditMode && !selectedManagerSubject) {
      invalidTargets.push("subject");
    }
    if (!selectedManagerTypeOption) {
      invalidTargets.push("managerType");
    }
    if (
      selectedManagerTypeOption?.value === "department_manager" &&
      selectedDepartments.length === 0
    ) {
      invalidTargets.push("departments");
    }
    setValidationErrors(invalidTargets);
    if (invalidTargets.length > 0) {
      focusFirstInvalidField({}, invalidTargets);
      return;
    }

    let companySubjectIdForSubmit = companySubjectId;

    if (subdomain === "manage" && selectedChildCompany.subjectId !== "") {
      companySubjectIdForSubmit = selectedChildCompany.subjectId;
    }

    const result: any = {
      company_subject: companySubjectIdForSubmit,
      subject: isEditMode ? editingManager.subject.id : selectedManagerSubject,
      manager_type: selectedManagerTypeOption.value,
    };
    const additionalUrlParameters: any = {};
    if (subdomain === "manage") {
      if (selectedChildCompany.subjectId !== "") {
        additionalUrlParameters["impersonate_subject"] =
          selectedChildCompany.subjectId;
        result.impersonate_subject = selectedChildCompany.subjectId;
      } else
        additionalUrlParameters["impersonate_subject"] =
          managerCompany.subjectId;
    }

    const departmentIds = selectedDepartments.map((dept: any) => dept.value);
    if (isEditMode) {
      result.managed_department_set = departmentIds;
    } else if (departmentIds.length !== 0) {
      result.managed_department_set = departmentIds;
    }

    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    api.genericApiRequest({
      method: isEditMode ? "put" : "post",
      entity: "companyManager",
      entityId: isEditMode ? editingManager.id : undefined,
      parametersToRender: { additionalUrlParameters: additionalUrlParameters },
      successHandler: () => {
        setNotificationMessage(t("save_successful"));
        setNotificationVariant("success");
        setShowNotification(true);
        onCloseDialog();
        setRefreshCompanyManagerTable((prev) => !prev);
        onManagersChanged?.();
      },
      failHandler: (error: any) => {
        handleError(error);
      },
      submitData: result,
      config: config,
    });
  };

  const additionalParameters: any = {};
  const additionalQuickSearchParameter: any = {};
  additionalQuickSearchParameter["include_highest_group"] = "true";
  if (subdomain === "admin") {
    additionalQuickSearchParameter["company_id"] = companyId; //TODO @Ben, we check the existence of companyId below, why not here?
  }
  //TODO @Nikita on admin there is always an companyId because we have to select a company before we arrive at this view
  if (subjectSearchAllCompanies) {
    delete additionalQuickSearchParameter["company_id"];
  }

  if (companyId !== undefined) {
    additionalParameters["company_subject"] = companySubjectId;
    additionalQuickSearchParameter["hide_managers_from_company_subject_id"] =
      companySubjectId;
  }

  if (subdomain === "admin") {
    additionalParameters["include_ancestor_managers"] = "true";
  }

  const additionalUrlParameter = useMemo(
    () => additionalParameters,
    [companyId, subdomain]
  );

  const isDepartmentManagerTypeSelected =
    selectedManagerTypeOption?.value === "department_manager";

  const content = (
    <Stack className={style.stackFormWrapper}>
      {!isEditMode && (
        <>
          {subdomain === "admin" && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={subjectSearchAllCompanies}
                  onChange={handleCheckboxChange}
                />
              }
              label={t("subject_search_all_companies")}
            />
          )}
          <div
            className={style.dropDownSubjectSearch}
            ref={registerValidationTarget("subject")}>
            <FormFieldLabel
              label={t("employee_search_hint")}
              htmlFor="company-manager-subject"
            />
            <DropDownSubjectSearch
              name="company-manager-subject"
              subdomain={subdomain}
              useGlobalState={false}
              allowSubjectSearchForNonAdmin={
                subdomain === "manage" ? true : false
              }
              options={[]}
              onChange={handleSubjectSelect}
              queryParameter="users"
              additionalSearchParameters={additionalQuickSearchParameter}
              error={validationErrors.includes("subject")}
            />
            {validationErrors.includes("subject") && (
              <Typography color="error" variant="caption" role="alert">
                {t("field_required")}
              </Typography>
            )}
          </div>
        </>
      )}
      <div ref={registerValidationTarget("managerType")}>
        <DropDown
          key={`manager-type-${editingManager?.id ?? "create"}`}
          label={t("type")}
          options={managerOptions}
          isMulti={false}
          isClearable={true}
          defaultValue={selectedManagerTypeOption?.value}
          onChange={handleSelectedManagerOptionChange}
        />
        {validationErrors.includes("managerType") && (
          <Typography color="error" variant="caption" role="alert">
            {t("field_required")}
          </Typography>
        )}
      </div>
      {isDepartmentManagerTypeSelected && (
        <div ref={registerValidationTarget("departments")}>
          {departmentOptions.length !== 0 && (
            <DropDown
              key={`managed-departments-${editingManager?.id ?? "create"}`}
              label={t("departments")}
              options={departmentOptions}
              isClearable={true}
              isMulti={true}
              defaultValue={selectedDepartments.map((dept: any) => dept.value)}
              onChange={handleSelectedDepartmentsChange}
            />
          )}
          {validationErrors.includes("departments") && (
            <Typography color="error" variant="caption" role="alert">
              {t("field_required")}
            </Typography>
          )}
        </div>
      )}
      <DialogActions className={style.dialogActions}>
        <GenericButton variant="outlined" onClick={onCloseDialog}>
          {t("cancel")}
        </GenericButton>
        <GenericButton type="submit">
          {submitButtonText}
        </GenericButton>
      </DialogActions>
    </Stack>
  );

  const cancelClose = useModalDismiss(openCreateModal, onCloseDialog);

  return (
    <>
      <GenericCrudTable
        columns={managerColumns}
        entity="companyManager"
        entityAdapter={companyManagerEntityAdapter}
        companySubjectId={companySubjectId}
        additionalUrlParameter={additionalUrlParameter}
        customCreateHandler={handleOpenCreate}
        uploadSuccessful={refreshCompanyManagerTable}
        refreshTableAfterAction={() => {
          onManagersChanged?.();
        }}
        showColumnFilterbyDefault
        customEditHandler={(row: any) => handleOpenEdit(row)}
        readOnlyMode={currentManagerType === "department_manager"}
      />
      <Dialog
        open={openCreateModal}
        {...cancelClose}
        PaperProps={{ className: style.paperProps }}
      >
        <DialogTitle className={style.dialogTitle}>{title}</DialogTitle>
        <form
          style={{ display: "contents" }}
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            handleSubmit();
          }}>
          <DialogContent className={style.dialogContent}>{content}</DialogContent>
        </form>
      </Dialog>
      {errorModal}
      <GenericNotification
        message={showNotification ? notificationMessage : null}
        variant={notificationVariant}
        handleCloseSnackbar={resetStateOnCloseNotification}
      />
    </>
  );
};

export default CompanyManagerTable;
